import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { EyeIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import { api, InvestigationAccess } from "./api";

// Staff view: a Police Narratives AI staff account opening a customer's
// investigation from the staff portal (/agency-management → agency →
// Investigations). The server decides (it checks the real session against
// SUPER_ADMIN_EMAILS and refuses every write and export); this context only
// lets the screens hide the controls that would be refused.

const StaffViewContext = createContext<InvestigationAccess | null>(null);

/** Truthy only while viewing someone else's investigation as staff. */
export function useStaffView(): InvestigationAccess | null {
  return useContext(StaffViewContext);
}

// Only our own accounts can be staff, so nobody else pays for the lookup.
const STAFF_DOMAIN = "@policenarratives.ai";

/**
 * Resolves staff access for the investigation in the URL. `checking` is true
 * until the answer is in, so the router can hold the upsell back for a staff
 * account without a seat.
 */
export function useStaffAccess(): { access: InvestigationAccess | null; checking: boolean } {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const investigationId = /^\/investigations\/([^/]+)/.exec(pathname)?.[1] || null;
  const eligible = Boolean(investigationId && user?.email?.toLowerCase().endsWith(STAFF_DOMAIN));
  const [resolved, setResolved] = useState<{ id: string; access: InvestigationAccess | null } | null>(null);

  useEffect(() => {
    if (!eligible || !investigationId) return;
    let cancelled = false;
    api
      .access(investigationId)
      .then((a) => !cancelled && setResolved({ id: investigationId, access: a.staffView ? a : null }))
      .catch(() => !cancelled && setResolved({ id: investigationId, access: null }));
    return () => {
      cancelled = true;
    };
  }, [eligible, investigationId]);

  if (!eligible) return { access: null, checking: false };
  if (resolved?.id !== investigationId) return { access: null, checking: true };
  return { access: resolved.access, checking: false };
}

export const StaffViewProvider: React.FC<{ access: InvestigationAccess | null; children: React.ReactNode }> = ({ access, children }) => (
  <StaffViewContext.Provider value={access}>{children}</StaffViewContext.Provider>
);

export const StaffViewBanner: React.FC = () => {
  const access = useStaffView();
  if (!access) return null;
  const owner = access.owner?.name || access.owner?.email;
  return (
    <div className="shrink-0 flex items-center gap-2 px-6 py-1.5 text-xs bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-400 border-b border-amber-200 dark:border-amber-900">
      <EyeIcon className="size-4 shrink-0" />
      <span>
        <b>Staff view, read-only.</b> {access.agencyName ? `${access.agencyName} · ` : ""}
        {owner ? `Owned by ${owner}. ` : ""}Edits, exports, and chat are off.
      </span>
    </div>
  );
};
